import Button from "components/buttons";
import Divider from "components/divider";
import DynamicFormInput from "components/form/form";
import FileUpload from "components/FileUpload";
import Notes from "components/notes";
import PageTitle from "components/pageTitle";
import TwoColumnLayout from "layouts/TwoColumnLayout";
import ConfirmationModal from "components/ConfirmationModal1";
import { ChangeEvent, useState } from "react";
import { useToast } from "contexts/ToastContext";
import { LoadingIcon } from "utils/constant";
import { useTranslation } from "react-i18next";

import {
  createLegalResearchRequest,
  updateLegalResearchRequest,
} from "services/legalResearchService";
import {
  LEGAL_RESEARCH_REQUEST_STATUS,
  LEGAL_RESEARCH_REQUEST_TYPES,
  LEGISLATIVE_DRAFTING_REQUEST_STATUS,
  TokenPayload,
} from "utils/constantValues";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import {
  createLegislativeDrafting,
  updateLegislativeDrafting,
} from "services/legislativeService";
import {
  createLegalAdvisory,
  updateLegalAdvisory,
} from "services/legalAdvisoryService";
import { jwtDecode } from "jwt-decode";

const CreateLegalAdvisoryRequest = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const navigate = useNavigate();
  const contextData = useAuth();

  const location = useLocation();
  const editData = location.state?.request;

  const [formState, setFormState] = useState({
    legal_request_summary: editData?.legal_request_summary || "",
    request_letter_reference_number:
      editData?.request_letter_reference_number || "",
    request_letter: null as File | null,
    legal_request: null as File | null,
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const handleChange = (
    e:
      | ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | ChangeEvent<HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleFileChange = (
    file: any,
    fieldName: keyof Pick<typeof formState, "request_letter" | "legal_request">
  ) => {
    setFormState((prev) => ({
      ...prev,
      [fieldName]: file,
    }));
    if (errors[fieldName]) {
      setErrors((prev) => ({ ...prev, [fieldName]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!formState.legal_request) {
      newErrors.legal_request = t("LegalAdvisoryRequestRequired") || "Legal Advisory Request is required";
    }
    if (!formState.request_letter_reference_number) {
      newErrors.request_letter_reference_number = t("RequestLetterReferenceNumberRequired") || "Request Letter Reference Number is required";
    }
    if (!formState.legal_request_summary.trim()) {
      newErrors.legal_request_summary = t("LegalRequestSummaryRequired") || "Legal Request Summary is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleOpenConfirmModal = () => {
    if (validateForm()) {
      setShowConfirmModal(true);
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      setShowConfirmModal(false);
      return;
    }

    setSubmitting(true);

    const data = new FormData();
    for (const key in formState) {
      const value = formState[key as keyof typeof formState];
      if (value !== null) {
        if (value instanceof File) {
          data.append(key, value);
        } else {
          data.append(key, String(value));
        }
      }
    }
    data.append(
      "current_status",
      LEGISLATIVE_DRAFTING_REQUEST_STATUS.submitted.value
    );
    if (editData) {
      try {
        const response = await updateLegalAdvisory(editData.id, data);
        if (response.id) {
          navigate(`/legal-advisory/${response.id}`);
        }
        showToast(t("LegalAdvisorySentSuccess") || "Legal Advisory request sent successfully!", "success");
      } catch (error) {
        console.error("Error submitting request:", error);
        showToast(t("FailedToSendLegalAdvisory") || "Failed to send Legal Advisory request.", "error");
      } finally {
        setSubmitting(false);
        setShowConfirmModal(false);
      }
    } else {
      const decoded = jwtDecode<TokenPayload>(
        contextData.user.access_token || ""
      );
      data.append(
        "requested_by",
        contextData.user?.user_type === "external"
          ? "stakeholder"
          : contextData.user?.user_type
      );
      data.append("requested_by_id", contextData.user?.uu_id ?? "");
      data.append("first_name", decoded?.first_name || "");
      data.append("last_name", decoded?.last_name || "");
      data.append("email_user_organization", decoded?.email || "");
      data.append(
        "organization_kebele",
        decoded?.organization?.address?.kebele || ""
      );
      data.append(
        "organization_zone",
        decoded?.organization?.address?.zone || ""
      );
      data.append(
        "organization_state",
        decoded?.organization?.address?.state || ""
      );
      data.append("organization_name", decoded?.organization?.name || "");
      data.append("organization_type", decoded?.organization?.type || "");
      data.append("stakeholder_id", decoded?.organization?.uu_id || "");
      try {
        const response = await createLegalAdvisory(data);
        if (response.id) {
          navigate(`/legal-advisory/${response.id}`);
        }
        showToast(t("LegalAdvisorySentSuccess") || "Legal Advisory request sent successfully!", "success");
      } catch (error) {
        console.error("Error submitting request:", error);
        showToast(t("FailedToSendLegalAdvisory") || "Failed to send Legal Advisory request.", "error");
      } finally {
        setSubmitting(false);
        setShowConfirmModal(false);
      }
    }
  };

  return (
    <div>
      <PageTitle
        icon="⚖"
        text={t("RequestForLegalAdvisory") || "Request for Legal Advisory"}
        subtitle={t("LegalAdvisorySubtitle") || "Lorem Ipsum is a placeholder text commonly used in the design and publishing industries as a temporary filler."}
        iconColor="text-purple-600"
        textColor="text-[#432101]"
      />
      <form>
        <TwoColumnLayout
          left={
            <div className="py-[22px] px-[24px] bg-white rounded-xl">
              <div className="grid gap-6 my-6 md:grid-cols-2">
                <div>
                  <FileUpload
                    label={t("RequestLetter") || "Request Letter"}
                    onChange={(file) => handleFileChange(file, "request_letter")}
                  />
                  {errors.request_letter && <p className="mt-1 text-xs text-red-500">{errors.request_letter}</p>}
                </div>
                <div>
                  <FileUpload
                    label={t("LegalAdvisoryRequestRequired") || "Legal Advisory Request*"}
                    required={true}
                    onChange={(file) => handleFileChange(file, "legal_request")}
                  />
                  {errors.legal_request && <p className="mt-1 text-xs text-red-500">{errors.legal_request}</p>}
                </div>
              </div>
              <DynamicFormInput
                inputs={[
                  {
                    value: formState.request_letter_reference_number,
                    type: "text",
                    label: t("RequestLetterReferenceNumber") || "Request Letter Reference Number",
                    name: "request_letter_reference_number",
                    onChange: handleChange,
                    placeholder: "#123",
                    error: errors.request_letter_reference_number,
                  },
                  {
                    value: formState.legal_request_summary,
                    type: "textarea",
                    label: t("LegalRequestSummary") || "Legal Request Summary",
                    name: "legal_request_summary",
                    onChange: handleChange,
                    placeholder: t("DetailHere") || "Detail here ...",
                    error: errors.legal_request_summary,
                  },
                ]}
              />
              <br />
              <div className="flex space-x-4 justify-between">
                <Button
                  variant="secondary"
                  onClick={() => {
                    navigate(-1);
                  }}
                >
                  {t("Cancel") || "Cancel"}
                </Button>
                <Button
                  variant="primary"
                  onClick={submitting ? undefined : handleOpenConfirmModal}
                >
                  {submitting ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                      </svg>
                      {t("Submitting") || "Submitting..."}
                    </span>
                  ) : t("Submit") || "Submit"}
                </Button>
              </div>
            </div>
          }
          right={
            <div className="w-full max-w-md space-y-4">
              <Notes
                title={t("Note") || "Note"}
                text={t("LegalAdvisoryNoteText") || "Please ensure all required documents are uploaded and information is accurately provided for your Legal Advisory request."}
                subtext={t("LegalAdvisoryNoteSubtext") || "The Legal Advisory process helps in obtaining expert legal opinions and guidance on specific legal matters."}
              />
              <div className="bg-white rounded-xl shadow-sm p-4 space-y-4">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="font-semibold">{t("DocumentRequirements") || "Document Requirements"}</h2>
                    <p className="text-sm text-gray-500">
                      {t("DocumentRequirementsDescription") || "Ensure you have the following documents ready for submission."}
                    </p>
                  </div>
                </div>
                <div className="border-t pt-2 space-y-2">
                  <div className="flex justify-between items-center p-2 hover:bg-gray-100 rounded-md cursor-pointer">
                    <span className="text-sm font-medium">
                      {t("LegalAdvisoryRequestDocument") || "Legal Advisory Request Document"}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-2 hover:bg-gray-100 rounded-md cursor-pointer">
                    <span className="text-sm font-medium">
                      {t("RequestLetter") || "Request Letter"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          }
        />
      </form>
      <ConfirmationModal
        isOpen={showConfirmModal}
        isLoading={submitting}
        onCancel={() => setShowConfirmModal(false)}
        onConfirm={handleSubmit}
        title={t("ConfirmSubmission") || "Confirm Submission"}
        message={t("ConfirmSubmissionMessage") || "Please review all entered information carefully. Once confirmed, this action will be submitted and cannot be modified."}
        confirmText={t("Submit") || "Submit"}
        cancelText={t("Cancel") || "Cancel"}
        type="success"
      />
    </div>
  );
};

export default CreateLegalAdvisoryRequest;